import React, { useState, useRef } from "react";
import {
  Button,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Box,
  Typography,
  ListSubheader,
  Collapse,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import {useTranslation} from "react-i18next";

const organizeItemsIntoCategories = (items = [], gid, uid) => {
  const categories = {};

  items.forEach((item) => {
    const hasAllowedGroupFilter = item.filters && 
      item.filters.some(filter => 
        filter.allowed_groups && 
        filter.allowed_groups.length > 0
      );

    const hasAllowedUserFilter = item.filters &&
      item.filters.some(filter =>
        filter.allowed_users &&
        filter.allowed_users.length > 0
      );

    if (!hasAllowedGroupFilter && !hasAllowedUserFilter) {
      if (!categories[item.category]) {
        categories[item.category] = [];
      }
      categories[item.category].push(item);
      return;
    }

    const isGroupAllowed = item.filters.some(filter => 
      filter.allowed_groups && 
      filter.allowed_groups.includes(gid)
    );

    const isUserAllowed = item.filters.some(filter =>
      filter.allowed_users &&
      filter.allowed_users.includes(uid)
    );

    if (isGroupAllowed || isUserAllowed) {
      if (!categories[item.category]) {
        categories[item.category] = [];
      }
      categories[item.category].push(item);
    }
  });

  return categories;
};

const PaperDropDown = styled(Paper)(() => ({
  position: "absolute",
  width: "100%",
  right: "0",
  minWidth: "250px",
  border: "1px solid #EAEAEA",
  boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.25)",
  borderRadius: "4px",
  zIndex: 4,
  "&:after": {
    content: '""',
    display: "block",
    width: "0",
    height: "0",
    borderLeft: "12px solid transparent",
    borderRight: "12px solid transparent",
    borderBottom: "12px solid white",
    position: "absolute",
    right: "52px",
    top: "-12px",
  },
  "& .MuiListItem-root": {
    padding: "0 16px 0 16px",
    "& .MuiListItemButton-root": {
      padding: "16px 0 16px 0",
      borderBottom: "1px solid #EAEAEA",
      transition: "all .5s ease-in-out",
      "&:hover": {
        background:
          "linear-gradient(85deg, transparent,rgb(169 235 211 / 42%), transparent, transparent)",
        transition: "all .5s ease-in-out",
      },
    },
    "&:last-child .MuiListItemButton-root": {
      padding: "16px 0 16px 0",
      borderBottom: "none",
    },
  },
  "& .category-header": {
    background: "#f5f5f5",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "12px 16px",
    borderBottom: "1px solid #EAEAEA",
  },
}));



const CategoryItem = ({ category, items, handleClick, openCategory, setOpenCategory }) => {
  const isOpen = openCategory === category || openCategory === "all";

  const toggleOpen = (e) => {
    e.stopPropagation();
    setOpenCategory(isOpen ? null : category);
  };

  return (
    <>
      <div className="category-header" onClick={toggleOpen}>
        <Typography variant="subtitle1">{category}</Typography>
        {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
      </div>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List disablePadding>
          {items.map((item, idx) => (
            <ListItem
              key={idx}
              disablePadding
              onClick={() => handleClick(item)}
            >
              <ListItemButton sx={{ pl: 4 }}>
                <Box>
                  <Typography variant="reportDisplayTextRoboto">
                    {item.title}
                  </Typography>
                  {item?.dataSource && (
                    <Typography
                      variant="reportDisplayTextRoboto"
                      component="div"
                      sx={{
                        mt: "8px",
                        fontSize: "11px",
                        fontStyle: "italic",
                        fontWeight: "",
                      }}
                    >
                      {item?.dataSource}
                    </Typography>
                  )}
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
};

const getApiGatedCategories = (items = []) => {
  const gated = new Set();
  items.forEach(item => {
    if (item.filters?.some(f => f.use_api)) {
      gated.add(item.category);
    }
  });
  return gated;
};

const CustomDropDown = (props) => {
  const route = useSelector((state) => state.route);
  const items = route?.jsonData?.items || [];
  const [show, setShow] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const [searchText, setSearchText] = useState("");
  const ref = useRef();
  const {t} = useTranslation();

  // Organize items into categories
  const categorizedItems = organizeItemsIntoCategories(items, route.userGroupId, route.userId);
  const categoryVisibility = route.categoryVisibility;
  const apiGatedCategories = getApiGatedCategories(items);

  // Filter items based on search text
  const filteredItems = React.useMemo(() => {
    if (!searchText.trim()) return categorizedItems;

    const filtered = {};
    Object.entries(categorizedItems).forEach(([category, items]) => {
      const matchingItems = items.filter(item => 
        item.title.toLowerCase().includes(searchText.toLowerCase()) ||
        (item.dataSource && item.dataSource.toLowerCase().includes(searchText.toLowerCase()))
      );

      if (matchingItems.length > 0) {
        filtered[category] = matchingItems;
      }
    });

    return filtered;
  }, [categorizedItems, searchText]);

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setShow(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Auto-expand categories when searching
  React.useEffect(() => {
    if (searchText.trim()) {
      setOpenCategory("all");
    }
  }, [searchText]);

  return (
    <Box ref={ref} sx={{ position: "relative" }}>
      <Button
        disabled={props.disable}
        onClick={() => setShow((prev) => !prev)}
        variant="contained"
        disableElevation
        color="success"
        sx={{
          background: "#00AD6F",
          paddingLeft: "53px",
          paddingRight: "53px",
          textTransform: "none !important",
        }}
      >
        {t('nav.newReport')}
        {!show ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
      </Button>
      {show && (
        <PaperDropDown>
          <Box sx={{ padding: "16px 16px 8px 16px" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search reports..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <List
            sx={{
              width: "100%",
              maxWidth: 360,
              overflow: "auto",
              maxHeight: "70vh",
            }}
          >
            {Object.keys(filteredItems).length > 0 ? (
              Object.entries(filteredItems)
              .filter(([category]) => 
                !apiGatedCategories.has(category) || categoryVisibility[category] === true
              )
              .map(
                ([category, items], index) => (
                  <CategoryItem
                    key={index}
                    category={category}
                    items={items}
                    handleClick={props.handleClick}
                    openCategory={openCategory}
                    setOpenCategory={setOpenCategory}
                  />
                )
              )
            ) : (
              <Box sx={{ padding: "16px", textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No reports found
                </Typography>
              </Box>
            )}
          </List>
          {Object.keys(filteredItems).length > 0 && (
            <ListSubheader style={{ textAlign: "center" }}>
              {t('nav.cta.scrollForMore')}
            </ListSubheader>
          )}
        </PaperDropDown>
      )}
    </Box>
  );
};

export default CustomDropDown;
